import { z } from 'zod';

export * from './monetization.js';

export const channelSchema = z.enum(['web', 'toss']);
export type Channel = z.infer<typeof channelSchema>;

export const evidenceGradeSchema = z.enum(['A', 'B', 'C']);
export type EvidenceGrade = z.infer<typeof evidenceGradeSchema>;

export const moneyRangeSchema = z.object({
  min: z.number().int().nonnegative(),
  max: z.number().int().nonnegative(),
}).refine(({ min, max }) => min <= max, 'min must not exceed max');
export type MoneyRange = z.infer<typeof moneyRangeSchema>;

export const apiErrorSchema = z.object({
  error: z.object({
    code: z.string().min(1),
    message: z.string().min(1),
    retryable: z.boolean(),
    requestId: z.string().min(1),
    details: z.record(z.string(), z.unknown()).optional(),
  }),
});
export type ApiError = z.infer<typeof apiErrorSchema>;

export const analyticsEventNames = [
  'app_open', 'onboarding_started', 'first_allowance_viewed', 'schedule_filled',
  'ocr_try', 'ocr_result_shown', 'ocr_accept', 'ocr_correct', 'report_view',
  'reconcile_done', 'pdf_generated', 'stats_view', 'share_card', 'shelter_view',
  'shelter_post', 'shelter_react', 'paywall_view', 'purchase_confirmed',
  'segment_selected', 'wage_term_confirmed', 'weekly_holiday_computed',
  'deposit_reconciled', 'citation_shown', 'evidence_pdf_generated',
  'first_job_started', 'first_job_contract_checked', 'first_job_shift_saved',
  'first_job_consultation_prepared',
] as const;

export const analyticsEventSchema = z.object({
  id: z.uuid(),
  name: z.enum(analyticsEventNames),
  channel: channelSchema,
  appVersion: z.string().min(1).max(40),
  contractVersion: z.string().min(1).max(40),
  releaseId: z.string().min(1).max(80),
  occurredAt: z.iso.datetime(),
  properties: z.record(z.string(), z.union([z.string().max(80), z.number(), z.boolean(), z.null()])),
}).superRefine(({ properties }, context) => {
  const forbidden = /(amount|salary|wage|body|memo|token|secret|stack|image)/i;
  for (const key of Object.keys(properties)) {
    if (forbidden.test(key)) context.addIssue({ code: 'custom', message: `forbidden analytics property: ${key}` });
  }
});
export type AnalyticsEvent = z.infer<typeof analyticsEventSchema>;

export interface CapabilitySet {
  ocr: boolean;
  cloudBackup: boolean;
  shelter: boolean;
  webPush: boolean;
  tossLogin: boolean;
  tossSmartMessage: boolean;
  payment: false;
  ads: false;
}

export const lockedCapabilities: Pick<CapabilitySet, 'payment' | 'ads'> = {
  payment: false,
  ads: false,
};

export const installSessionSchema = z.object({
  installId: z.string().regex(/^ins_[A-Za-z0-9_-]{16,}$/),
  installToken: z.string().min(32).max(128),
});
export type InstallSession = z.infer<typeof installSessionSchema>;

export const healthSchema = z.object({
  ok: z.literal(true),
  version: z.string().min(1),
  requestId: z.string().min(1),
});

const backupCryptoFields = {
  schemaVersion: z.literal(1),
  salt: z.string().regex(/^[A-Za-z0-9_-]{22}$/),
  nonce: z.string().regex(/^[A-Za-z0-9_-]{16}$/),
  ciphertext: z.string().min(20).max(8 * 1024 * 1024).regex(/^[A-Za-z0-9_-]+$/).refine((value) => value.length % 4 !== 1),
  checksum: z.string().regex(/^[0-9a-f]{64}$/),
} as const;

export const encryptedBackupEnvelopeSchema = z.object({ ...backupCryptoFields, lookupKey: z.string().regex(/^[A-Za-z0-9_-]{32,128}$/) }).strict();
export type EncryptedBackupEnvelope = z.infer<typeof encryptedBackupEnvelopeSchema>;

export const backupUploadSchema = z.object({ ...backupCryptoFields, ciphertext: backupCryptoFields.ciphertext.max(1_333_334), size: z.number().int().positive().max(1_000_000) }).strict().superRefine((value, context) => {
  if (Math.floor(value.ciphertext.length * 6 / 8) !== value.size) context.addIssue({ code: 'custom', path: ['size'], message: 'ciphertext size mismatch' });
});

export const cloudBackupResponseSchema = z.object({ ...backupCryptoFields, ciphertext: backupCryptoFields.ciphertext.max(1_333_334), size: z.number().int().positive().max(1_000_000), updatedAt: z.number().int().positive() }).strict().superRefine((value, context) => {
  if (Math.floor(value.ciphertext.length * 6 / 8) !== value.size) context.addIssue({ code: 'custom', path: ['size'], message: 'ciphertext size mismatch' });
});

export const ocrResponseSchema = z.object({
  days: z.array(z.object({ date: z.iso.date(), shiftTypeId: z.string().min(1).max(80), matchedShort: z.string().min(1).max(8), confidence: z.number().min(0).max(1) })).max(31),
  omittedCount: z.number().int().nonnegative().max(31), modelVersion: z.string().min(1).max(120), requestId: z.string().min(1),
});
export type OcrResponse = z.infer<typeof ocrResponseSchema>;

export const reminderPreferenceSchema = z.object({
  paydayDay: z.union([z.number().int().min(1).max(31), z.literal('last')]), agreed: z.literal(true),
  quietStartHour: z.number().int().min(0).max(23), quietEndHour: z.number().int().min(0).max(23),
  timeZone: z.literal('Asia/Seoul'),
}).strict();
export type ReminderPreference = z.infer<typeof reminderPreferenceSchema>;

export const webPushSubscriptionSchema = reminderPreferenceSchema.extend({
  platform: z.literal('web'),
  subscription: z.object({ endpoint: z.url().max(2048), expirationTime: z.number().nullable(), keys: z.object({ p256dh: z.string().min(20).max(256), auth: z.string().min(8).max(128) }) }).strict(),
}).strict();

export const contentCardSchema = z.object({
  id: z.string().min(1), kind: z.enum(['tip', 'law', 'job']), title: z.string().min(1).max(80), body: z.string().min(1).max(1000),
  sourceUrl: z.url().nullable(), sourceTitle: z.string().nullable(), sourceCheckedAt: z.number().int().positive().nullable(), publishedAt: z.number().int().positive(),
});
export type ContentCard = z.infer<typeof contentCardSchema>;

export const contentCardInputSchema = z.object({
  kind: z.enum(['tip', 'law', 'job']),
  title: z.string().trim().min(1).max(80),
  body: z.string().trim().min(1).max(1000),
  sourceUrl: z.url().max(2048).optional(),
  sourceTitle: z.string().trim().min(1).max(160).optional(),
  sourceCheckedAt: z.number().int().positive().optional(),
}).strict().superRefine((value, context) => {
  if (value.kind === 'law' && (!value.sourceUrl?.startsWith('https://') || !value.sourceTitle || !value.sourceCheckedAt)) {
    context.addIssue({ code: 'custom', message: 'law source required' });
  }
});

export const contentFeedSchema = z.object({
  cards: z.array(contentCardSchema).max(20),
  nextCursor: z.string().min(20).max(2048).nullable(),
}).strict();

export const rateTableKinds = ['minWage', 'publicServantRates', 'holidays', 'fourInsurance', 'privateRulePolicy'] as const;
export const rateTableKindSchema = z.enum(rateTableKinds);
export const rateTableInputSchema = z.object({
  year: z.number().int().min(2025).max(2100),
  kind: rateTableKindSchema,
  version: z.string().trim().min(1).max(80),
  schemaVersion: z.literal(1),
  data: z.record(z.string(), z.unknown()),
  effectiveFrom: z.iso.date(),
  effectiveTo: z.iso.date().optional(),
  sourceUrl: z.url().refine((value) => value.startsWith('https://')),
  sourceCheckedAt: z.number().int().positive(),
  checksum: z.string().regex(/^[a-f0-9]{64}$/),
}).strict().refine((value) => !value.effectiveTo || value.effectiveTo >= value.effectiveFrom, {
  path: ['effectiveTo'], message: 'effectiveTo before effectiveFrom',
});

export const rateTableDraftResponseSchema = z.object({
  kind: rateTableKindSchema,
  version: z.string().min(1),
  status: z.literal('draft'),
  checksum: z.string().regex(/^[a-f0-9]{64}$/),
  updatedAt: z.number().int().positive(),
}).strict();

export const sourceRefSchema: z.ZodType<unknown> = z.custom(isSourceRef, 'invalid source reference');
export const privateRulePolicySchema: z.ZodType<unknown> = z.custom(isPrivateRulePolicy, 'invalid private rule policy');
export const publicServantRateSchema: z.ZodType<unknown> = z.custom(isPublicServantRate, 'invalid public servant rate');
export const rateTablesSchema: z.ZodType<unknown> = z.custom(isRateTables, 'invalid rate tables');
export const remoteRateTableResponseSchema: z.ZodType<unknown> = z.custom(isRemoteRateTableResponse, 'invalid remote rate table response');

export const taxPresetSchema = z.enum(['fourInsurance', 'businessIncome3_3', 'employmentOnly', 'none']);
export type TaxPreset = z.infer<typeof taxPresetSchema>;
export const segmentSchema = z.enum(['shift', 'hourly']);
export type Segment = z.infer<typeof segmentSchema>;
export const hourlyPresetSchema = z.enum(['convenienceStore', 'cafe', 'logistics', 'tutoring']);
export type HourlyPreset = z.infer<typeof hourlyPresetSchema>;

export const firstJobCheckStatusSchema = z.enum(['unknown', 'checked', 'not_applicable']);
export type FirstJobCheckStatus = z.infer<typeof firstJobCheckStatusSchema>;
export const firstJobPlanSchema = z.object({
  ageBand: z.enum(['under18', 'adultTeen', 'unknown']),
  usualShift: z.object({
    start: z.string().regex(/^\d{2}:\d{2}$/),
    end: z.string().regex(/^\d{2}:\d{2}$/),
    breakMinutes: z.number().int().min(0).max(720).optional(),
  }),
  contractChecklist: z.object({
    contractCopy: firstJobCheckStatusSchema,
    payTerms: firstJobCheckStatusSchema,
    scheduleAndBreak: firstJobCheckStatusSchema,
    paydayAndMethod: firstJobCheckStatusSchema,
    guardianDocuments: firstJobCheckStatusSchema,
  }),
  startedAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
}).strict();
export type FirstJobPlan = z.infer<typeof firstJobPlanSchema>;

export const workplaceSchema = z.object({
  id: z.string().min(1).max(80), name: z.string().trim().min(1).max(40),
  colorToken: z.string().trim().min(1).max(40), scale: z.enum(['under5', 'fivePlus', 'unknown']),
  scaleConfirmedAt: z.iso.datetime().nullable(), segment: segmentSchema,
  archivedAt: z.iso.datetime().nullable(), createdAt: z.iso.datetime(),
});
export type Workplace = z.infer<typeof workplaceSchema>;

export const wageTermSchema = z.object({
  id: z.string().min(1).max(80), workplaceId: z.string().min(1).max(80),
  effectiveFrom: z.iso.date(), ordinaryHourlyRate: z.number().int().positive(),
  scheduledDaysPerWeek: z.number().int().min(0).max(7), weeklyScheduledMinutes: z.number().int().min(0).max(2400),
  taxPreset: taxPresetSchema, confirmedAt: z.iso.datetime(), createdAt: z.iso.datetime(),
  includesWeeklyHolidayPay: z.boolean().optional(), weeklyHolidayDay: z.number().int().min(0).max(6).optional(),
  insuranceBaseMonthly: z.number().int().nonnegative().optional(),
});
export type WageTerm = z.infer<typeof wageTermSchema>;

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function hasOnlyKeys(value: Record<string, unknown>, allowed: string[]): boolean {
  return Object.keys(value).every((key) => allowed.includes(key));
}

function isIsoDate(value: unknown): value is string {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(Date.parse(`${value}T00:00:00Z`));
}

function isIsoDateTime(value: unknown): value is string {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value) && !Number.isNaN(Date.parse(value));
}

function isHttps(value: unknown): value is string {
  if (typeof value !== 'string' || value.length > 2048) return false;
  return /^https:\/\/(?:\[[0-9a-f:.]+\]|[a-z0-9](?:[a-z0-9.-]*[a-z0-9])?)(?::\d{1,5})?(?:[/?#][^\s]*)?$/i.test(value);
}

function isSourceRef(value: unknown): boolean {
  return isRecord(value) && hasOnlyKeys(value, ['title', 'url', 'checkedAt'])
    && typeof value.title === 'string' && value.title.length > 0 && value.title.length <= 200
    && isHttps(value.url) && isIsoDateTime(value.checkedAt);
}

function isPremiumRate(value: unknown, denominator: 1 | 2): boolean {
  return isRecord(value) && hasOnlyKeys(value, ['numerator', 'denominator']) && value.numerator === 1 && value.denominator === denominator;
}

function isPrivateRulePolicy(value: unknown): boolean {
  if (!isRecord(value) || !hasOnlyKeys(value, ['version', 'effectiveFrom', 'source', 'overtimePremium', 'nightPremium', 'holidayPremium', 'holidayOverPremium', 'classificationReview'])) return false;
  if (typeof value.version !== 'string' || value.version.length === 0 || !isIsoDate(value.effectiveFrom) || !isSourceRef(value.source)) return false;
  if (!isPremiumRate(value.overtimePremium, 2) || !isPremiumRate(value.nightPremium, 2) || !isPremiumRate(value.holidayPremium, 2) || !isPremiumRate(value.holidayOverPremium, 1)) return false;
  if (!isRecord(value.classificationReview) || !hasOnlyKeys(value.classificationReview, ['dailyOvertimeAndHoliday', 'reviewedAt'])) return false;
  const state = value.classificationReview.dailyOvertimeAndHoliday;
  return state === 'legal_review_required' || (state === 'reviewed' && isIsoDateTime(value.classificationReview.reviewedAt));
}

function isPublicServantRate(value: unknown): boolean {
  return isRecord(value) && hasOnlyKeys(value, ['id', 'overtimeHourly', 'nightHourly', 'holidayDaily'])
    && typeof value.id === 'string' && value.id.length > 0 && value.id.length <= 80
    && [value.overtimeHourly, value.nightHourly, value.holidayDaily].every((entry) => typeof entry === 'number' && Number.isInteger(entry) && entry > 0);
}

const requiredRateTableProvenance = [
  'privateRulePolicy', 'publicServantRates', 'fourInsurance',
  'businessIncome3_3', 'employmentOnly', 'minWage',
] as const;

function isRateTableProvenance(value: unknown): boolean {
  return isRecord(value)
    && hasOnlyKeys(value, ['version', 'effectiveFrom', 'effectiveTo', 'sourceUrl', 'checkedAt', 'checksum'])
    && typeof value.version === 'string' && value.version.length > 0 && value.version.length <= 80
    && isIsoDate(value.effectiveFrom)
    && (value.effectiveTo === null || isIsoDate(value.effectiveTo))
    && (value.effectiveTo === null || value.effectiveTo >= value.effectiveFrom)
    && isHttps(value.sourceUrl)
    && isIsoDateTime(value.checkedAt)
    && typeof value.checksum === 'string' && /^[a-f0-9]{64}$/.test(value.checksum);
}

function isRateTables(value: unknown): boolean {
  if (!isRecord(value) || !hasOnlyKeys(value, ['schemaVersion', 'versions', 'provenance', 'privateRulePolicy', 'publicServantSource', 'publicServantRates', 'taxRates', 'minimumWage']) || value.schemaVersion !== 1) return false;
  const versions = value.versions;
  const provenance = value.provenance;
  if (!isRecord(versions) || !Object.values(versions).every((entry) => typeof entry === 'string' && entry.length > 0)) return false;
  if (!isRecord(provenance)
    || !hasOnlyKeys(provenance, [...requiredRateTableProvenance])
    || !requiredRateTableProvenance.every((kind) => isRateTableProvenance(provenance[kind]))) return false;
  if (!requiredRateTableProvenance.every((kind) => versions[kind] === (provenance[kind] as Record<string, unknown>).version)) return false;
  if (!isPrivateRulePolicy(value.privateRulePolicy) || !isSourceRef(value.publicServantSource) || !isRecord(value.publicServantRates)) return false;
  const ratesValid = Object.entries(value.publicServantRates).length > 0 && Object.entries(value.publicServantRates).every(([key, entry]) => isPublicServantRate(entry) && (entry as Record<string, unknown>).id === key);
  if (!ratesValid) return false;
  if (!isRecord(value.minimumWage) || !hasOnlyKeys(value.minimumWage, ['hourly', 'year', 'source']) || typeof value.minimumWage.hourly !== 'number' || value.minimumWage.hourly <= 0 || typeof value.minimumWage.year !== 'number' || !isSourceRef(value.minimumWage.source)) return false;
  if (!isRecord(value.taxRates) || !hasOnlyKeys(value.taxRates, ['fourInsurance', 'businessIncome3_3', 'employmentOnly'])) return false;
  const insurance = value.taxRates.fourInsurance;
  const business = value.taxRates.businessIncome3_3;
  const employment = value.taxRates.employmentOnly;
  return isRecord(insurance) && isRecord(business) && isRecord(employment)
    && hasOnlyKeys(insurance, ['nationalPensionRate', 'nationalPensionCap', 'healthInsuranceRate', 'longTermCareRateOfHealth', 'employmentRate'])
    && hasOnlyKeys(business, ['incomeTaxRate', 'localTaxRate'])
    && hasOnlyKeys(employment, ['employmentRate'])
    && [insurance.nationalPensionRate, insurance.nationalPensionCap, insurance.healthInsuranceRate, insurance.longTermCareRateOfHealth, insurance.employmentRate, business.incomeTaxRate, business.localTaxRate, employment.employmentRate].every((entry) => typeof entry === 'number' && Number.isFinite(entry) && entry >= 0);
}

function isRemoteRateTableResponse(value: unknown): boolean {
  if (!isRecord(value) || !hasOnlyKeys(value, ['year', 'schemaVersion', 'data', 'updatedAt', 'sourceRefs', 'fallback'])) return false;
  if (!Number.isInteger(value.year) || (value.year as number) < 2025 || (value.year as number) > 2100 || value.schemaVersion !== 1 || !isRecord(value.data) || typeof value.fallback !== 'boolean') return false;
  if (!(typeof value.updatedAt === 'number' && Number.isInteger(value.updatedAt) && value.updatedAt > 0) && !isIsoDateTime(value.updatedAt)) return false;
  if (!Array.isArray(value.sourceRefs) || value.sourceRefs.length > rateTableKinds.length) return false;
  const referencesValid = value.sourceRefs.every((entry) => isRecord(entry)
    && hasOnlyKeys(entry, ['kind', 'url', 'checkedAt', 'version', 'checksum', 'effectiveFrom', 'effectiveTo'])
    && rateTableKinds.includes(entry.kind as typeof rateTableKinds[number]) && isHttps(entry.url)
    && typeof entry.checkedAt === 'number' && Number.isInteger(entry.checkedAt) && entry.checkedAt > 0
    && typeof entry.version === 'string' && entry.version.length > 0 && entry.version.length <= 80
    && typeof entry.checksum === 'string' && /^[a-f0-9]{64}$/.test(entry.checksum)
    && isIsoDate(entry.effectiveFrom) && (entry.effectiveTo === null || isIsoDate(entry.effectiveTo)));
  if (!referencesValid) return false;
  if (value.fallback) return value.sourceRefs.length === 0 && isRateTables(value.data);
  const referencedKinds = value.sourceRefs.map((entry) => (entry as Record<string, unknown>).kind as string);
  const dataKinds = Object.keys(value.data);
  return referencedKinds.length > 0
    && new Set(referencedKinds).size === referencedKinds.length
    && dataKinds.length === referencedKinds.length
    && dataKinds.every((kind) => rateTableKinds.includes(kind as typeof rateTableKinds[number]) && referencedKinds.includes(kind));
}

export const copy = {
  homeEmpty: '근무표를 입력하면 이번 달 수당이 여기 보여요.',
  calendarEmpty: '아직 근무표가 비어 있어요. 날짜를 누르고 근무 유형을 골라주세요.',
  profileIncomplete: '확인이 필요한 정보가 있어요. 입력한 범위까지만 보여드릴게요.',
  reportEmpty: '이번 달 근무 기록이 없어요. 달력에서 입력해주세요.',
  offlineBadge: '오프라인 — 기록·계산은 계속 쓸 수 있어요',
  evidenceA: '입력 근거가 모두 확인된 참고값이에요.',
  evidenceB: '확인되지 않은 값이 있어 범위로 보여드려요.',
  evidenceC: '핵심 정보가 부족해 차액과 근거 문서를 만들 수 없어요.',
  legalNotice: '사용자가 입력·확인한 정보에 따른 참고값이며 법적 판단이나 법률 자문이 아니에요.',
  confirmationNotice: '이 문서는 이용자 본인이 입력한 근무 기록을 정리한 출력물입니다. 법적 판단이나 미지급 여부의 확정이 아니며, 작성 주체는 이용자 본인입니다.',
} as const;

export const privateProfileSchema = z.object({
  id: z.literal('me'),
  sector: z.literal('private'),
  job: z.enum(['nurse', 'factory', 'security', 'hospital', 'service', 'other']),
  ordinaryHourlyRate: z.number().int().positive().optional(),
  ordinaryWageConfirmed: z.boolean(),
  firmSize: z.enum(['under5', 'fivePlus', 'unknown']),
  workTimeScheme: z.enum(['standard', 'flexible', 'selective', 'discretionary', 'special', 'unknown']),
  establishmentConfirmed: z.boolean(),
  segment: segmentSchema.optional(),
  hourlyPreset: hourlyPresetSchema.optional(),
  workplaceId: z.string().min(1).max(80).optional(),
  firstJob: firstJobPlanSchema.optional(),
});

export const publicProfileSchema = z.object({
  id: z.literal('me'),
  sector: z.literal('public'),
  job: z.enum(['police', 'fire']),
  rateEntryId: z.string().min(1),
  institutionRuleConfirmed: z.boolean(),
  recognizedOvertimeMinutes: z.number().int().nonnegative(),
  recognizedNightMinutes: z.number().int().nonnegative(),
  recognizedHolidayDays: z.number().int().nonnegative(),
});

export const localProfileSchema = z.discriminatedUnion('sector', [privateProfileSchema, publicProfileSchema]);
export type LocalProfile = z.infer<typeof localProfileSchema>;

export const localShiftTypeSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1).max(20),
  short: z.string().min(1).max(4),
  start: z.string().regex(/^\d{2}:\d{2}$/),
  end: z.string().regex(/^\d{2}:\d{2}$/),
  breakMinutes: z.number().int().nonnegative().optional(),
  kind: z.enum(['work', 'off', 'leave', 'absence', 'etc']),
  countsAsNight: z.boolean().optional(),
  color: z.string().regex(/^#[0-9A-Fa-f]{6}$/),
  sortOrder: z.number().int(),
});
export type LocalShiftType = z.infer<typeof localShiftTypeSchema>;

export const localShiftSchema = z.object({
  date: z.iso.date(),
  shiftTypeId: z.string().min(1),
  actualConfirmed: z.boolean(),
  workplaceId: z.string().min(1).max(80).optional(),
  scheduledStart: z.string().regex(/^\d{2}:\d{2}$/).optional(),
  scheduledEnd: z.string().regex(/^\d{2}:\d{2}$/).optional(),
  actualStart: z.string().regex(/^\d{2}:\d{2}$/).optional(),
  actualEnd: z.string().regex(/^\d{2}:\d{2}$/).optional(),
  actualBreakMinutes: z.number().int().min(0).max(720).optional(),
  actualBreakUnconfirmed: z.boolean().optional(),
  actualBreakRanges: z.array(z.object({ start: z.string().regex(/^\d{2}:\d{2}$/), end: z.string().regex(/^\d{2}:\d{2}$/) })).max(12).optional(),
});
export type LocalShift = z.infer<typeof localShiftSchema>;

export const defaultShiftTypes: LocalShiftType[] = [
  { id: 'D', name: '데이', short: 'D', start: '07:00', end: '15:00', breakMinutes: 60, kind: 'work', color: '#0284c7', sortOrder: 10 },
  { id: 'E', name: '이브닝', short: 'E', start: '15:00', end: '23:00', breakMinutes: 60, kind: 'work', color: '#ea580c', sortOrder: 20 },
  { id: 'N', name: '나이트', short: 'N', start: '23:00', end: '07:00', breakMinutes: 60, kind: 'work', countsAsNight: true, color: '#4f46e5', sortOrder: 30 },
  { id: 'OFF', name: '오프', short: 'OFF', start: '00:00', end: '00:00', kind: 'off', color: '#64748b', sortOrder: 40 },
];

export const platformCapabilities: Record<Channel, CapabilitySet> = {
  web: { ocr: true, cloudBackup: true, shelter: true, webPush: true, tossLogin: false, tossSmartMessage: false, payment: false, ads: false },
  toss: { ocr: true, cloudBackup: true, shelter: true, webPush: false, tossLogin: false, tossSmartMessage: false, payment: false, ads: false },
};

export const shelterTagSchema = z.enum(['before', 'working', 'after', 'off']);
export const shelterJobBadgeSchema = z.enum(['nurse', 'factory', 'security', 'hospital', 'service', 'police', 'fire', 'etc']);
export const shelterReactionSchema = z.enum(['warm', 'cheer', 'hug']);
export const shelterReportReasonSchema = z.enum(['abuse', 'spam', 'privacy', 'self_harm', 'etc']);

export const shelterPostInputSchema = z.object({
  body: z.string().trim().min(1).max(80),
  tag: shelterTagSchema,
  jobBadge: shelterJobBadgeSchema,
  nicknameSnapshot: z.string().trim().min(3).max(30),
  turnstileToken: z.string().min(10).max(4096).optional(),
});

export const shelterFeedItemSchema = z.object({
  id: z.string().min(1),
  nickname: z.string().min(1),
  jobBadge: shelterJobBadgeSchema,
  tag: shelterTagSchema,
  body: z.string().max(80),
  createdAt: z.number().int().nonnegative(),
  reactions: z.object({ warm: z.number().int().nonnegative(), cheer: z.number().int().nonnegative(), hug: z.number().int().nonnegative() }),
  myReaction: shelterReactionSchema.nullable(),
  owned: z.boolean(),
});
export type ShelterFeedItem = z.infer<typeof shelterFeedItemSchema>;

export const shelterFeedSchema = z.object({
  items: z.array(shelterFeedItemSchema).max(20),
  nextCursor: z.string().min(20).max(2048).nullable(),
  awake: z.number().int().min(0).max(100),
}).strict();

export const shelterBlockSchema = z.object({ blockId: z.string().min(20).max(1024), nicknameSnapshot: z.string().min(1).max(30) }).strict();
export type ShelterBlock = z.infer<typeof shelterBlockSchema>;
export const shelterBlockListSchema = z.object({ items: z.array(shelterBlockSchema).max(500) }).strict();
